import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiKey } from '../../../types/organization.types'

interface ApiKeysState {
  keys: ApiKey[]
  loading: boolean
  error: string | null
  selectedKey: ApiKey | null
  generatingKey: boolean
  rotatingKeyId: string | null
  revokingKeyId: string | null
}

const initialState: ApiKeysState = {
  keys: [],
  loading: false,
  error: null,
  selectedKey: null,
  generatingKey: false,
  rotatingKeyId: null,
  revokingKeyId: null,
}

const apiKeysSlice = createSlice({
  name: 'organizationApiKeys',
  initialState,
  reducers: {
    // Fetch API keys
    fetchApiKeysStart(state) {
      state.loading = true
      state.error = null
    },
    fetchApiKeysSuccess(state, action: PayloadAction<ApiKey[]>) {
      state.loading = false
      state.keys = action.payload
      state.error = null
    },
    fetchApiKeysFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },

    // Generate API key
    generateApiKeyStart(state) {
      state.generatingKey = true
      state.error = null
    },
    generateApiKeySuccess(state, action: PayloadAction<ApiKey>) {
      state.generatingKey = false
      state.keys.push(action.payload)
      state.selectedKey = action.payload
      state.error = null
    },
    generateApiKeyFailure(state, action: PayloadAction<string>) {
      state.generatingKey = false
      state.error = action.payload
    },

    // Rotate API key
    rotateApiKeyStart(state, action: PayloadAction<string>) {
      state.rotatingKeyId = action.payload
      state.error = null
    },
    rotateApiKeySuccess(state, action: PayloadAction<ApiKey>) {
      state.rotatingKeyId = null
      const index = state.keys.findIndex((k) => k.id === action.payload.id)
      if (index !== -1) {
        state.keys[index] = action.payload
      }
      // Set as selected key to display the new secret key inline
      state.selectedKey = action.payload
      state.error = null
    },
    rotateApiKeyFailure(state, action: PayloadAction<string>) {
      state.rotatingKeyId = null
      state.error = action.payload
    },

    // Revoke API key
    revokeApiKeyStart(state, action: PayloadAction<string>) {
      state.revokingKeyId = action.payload
      state.error = null
    },
    revokeApiKeySuccess(state, action: PayloadAction<string>) {
      state.revokingKeyId = null
      state.keys = state.keys.filter((k) => k.id !== action.payload)
      state.error = null
    },
    revokeApiKeyFailure(state, action: PayloadAction<string>) {
      state.revokingKeyId = null
      state.error = action.payload
    },

    // Update rate limit
    updateRateLimitStart(state) {
      state.error = null
    },
    updateRateLimitSuccess(state, action: PayloadAction<ApiKey>) {
      const index = state.keys.findIndex((k) => k.id === action.payload.id)
      if (index !== -1) {
        state.keys[index] = action.payload
      }
      state.error = null
    },
    updateRateLimitFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
    },

    // Select key
    selectApiKey(state, action: PayloadAction<ApiKey | null>) {
      state.selectedKey = action.payload
    },

    // Clear error
    clearError(state) {
      state.error = null
    },

    // Reset state
    resetApiKeysState: () => initialState,
  },
})

export const {
  fetchApiKeysStart,
  fetchApiKeysSuccess,
  fetchApiKeysFailure,
  generateApiKeyStart,
  generateApiKeySuccess,
  generateApiKeyFailure,
  rotateApiKeyStart,
  rotateApiKeySuccess,
  rotateApiKeyFailure,
  revokeApiKeyStart,
  revokeApiKeySuccess,
  revokeApiKeyFailure,
  updateRateLimitStart,
  updateRateLimitSuccess,
  updateRateLimitFailure,
  selectApiKey,
  clearError,
  resetApiKeysState,
} = apiKeysSlice.actions

export default apiKeysSlice.reducer
